import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { ROUTES } from '../routes'
import Button from './Button'
import CounterpartyCrmSearch from './CounterpartyCrmSearch'
import SearchableSelect from './SearchableSelect'
import { EmptyState, InlineAlert, Skeleton, Status } from './ui'
import '../styles/reference-mapping.css'

const TYPE_LABELS = { company: 'Компания', contact: 'Контакт' }
const ORIGIN_LABELS = { deal: 'из сделки', search: 'из поиска', sync: 'загружена' }

// Вкладки справочника контрагентов. Открывается «Все», дальше — последняя
// выбранная в этом подключении (решение владельца 2026-10-01: пустая
// «Сопоставленные» после загрузки выглядела как отсутствие результата).
const DEFAULT_TAB = 'all'
const TAB_STORAGE_PREFIX = 'connector-hub:counterparty-tab'
const COUNTERPARTY_TABS = [
  { key: 'all', label: 'Все' },
  { key: 'mapped', label: 'Сопоставленные' },
  { key: 'unmapped', label: 'Несопоставленные' },
  { key: 'new', label: 'Новые несопоставленные' }
]
const TYPE_FILTERS = [
  { key: '', label: 'Все типы' },
  { key: 'company', label: 'Компании' },
  { key: 'contact', label: 'Контакты' }
]
const TYPE_FILTER_LABELS = { company: 'компании', contact: 'контакты' }
// Фильтр по сопоставлению — у сотрудников: вкладок у них нет.
const MAPPING_FILTERS = [
  { key: '', label: 'Все' },
  { key: 'mapped', label: 'Сопоставленные' },
  { key: 'unmapped', label: 'Несопоставленные' }
]
const MAPPING_FILTER_LABELS = { mapped: 'сопоставленные', unmapped: 'несопоставленные' }
const PAGE_SIZE = 50
const FILTER_DELAY_MS = 300
// Подсказка переключателя множественного выбора — вместо названия (решение
// владельца 2026-10-06, ADR R18).
const MULTI_SELECT_HINTS = {
  on: 'Одну запись Финтабло можно выбрать в нескольких строках.',
  off: 'Каждую запись Финтабло можно выбрать только в одной строке.'
}

// Выбор вкладки — удобство одного пользователя в его браузере. Хранилище
// бывает недоступно (приватное окно, запрет сайта) — тогда «Все».
function storedTab (key) {
  try {
    const value = globalThis.localStorage?.getItem(key)
    return COUNTERPARTY_TABS.some(tab => tab.key === value) ? value : null
  } catch (_) {
    return null
  }
}

function rememberTab (key, value) {
  try {
    globalThis.localStorage?.setItem(key, value)
  } catch (_) {}
}

function formatCount (value) {
  return Number(value || 0).toLocaleString('ru-RU')
}

function employeeRow (item, connector) {
  const sourceId = connector === 'amocrm' ? item.amocrm_user_id : item.b24_user_id
  return {
    key: String(sourceId),
    sourceId: String(sourceId),
    sourceName: connector === 'amocrm' ? item.amocrm_name : item.b24_name,
    sourceDescription: connector === 'amocrm' ? item.amocrm_email : item.b24_email,
    targetId: item.fintablo_responsible_id ? String(item.fintablo_responsible_id) : '',
    targetName: item.fintablo_name,
    sourceStale: item.is_stale === true,
    targetStale: item.target_stale === true,
    targetConflict: item.target_conflict === true
  }
}

function counterpartyRow (item) {
  return {
    key: `${item.b24_entity_type}:${item.b24_entity_id}`,
    sourceId: String(item.b24_entity_id),
    sourceName: item.b24_name,
    sourceType: item.b24_entity_type,
    targetId: item.fintablo_partner_id ? String(item.fintablo_partner_id) : '',
    targetName: item.fintablo_name,
    sourceStale: item.is_stale === true,
    targetStale: item.target_stale === true,
    targetConflict: item.target_conflict === true,
    isNew: item.is_new === true,
    origin: item.origin
  }
}

// Строки справочника amoCRM приходят в общей форме хаба (source_*/target_*).
function amocrmCounterpartyRow (item) {
  return {
    key: `${item.source_type}:${item.source_id}`,
    sourceId: String(item.source_id),
    sourceName: item.source_name,
    sourceType: item.source_type,
    targetId: item.target_id ? String(item.target_id) : '',
    targetName: item.target_name,
    sourceStale: item.is_stale === true,
    targetStale: item.target_stale === true,
    targetConflict: item.target_conflict === true,
    isNew: item.is_new === true,
    origin: item.origin
  }
}

// Справочник «Направления» (direction-by-pipeline): ключ строки — поле сделки
// и id значения; подпись направления — с родителем, одно название бывает на
// двух уровнях дерева.
function directionLabel (name, parentName) {
  return parentName ? `${parentName} / ${name}` : name
}

function directionRow (item) {
  return {
    key: `${item.field_key}:${item.value_id}`,
    sourceId: String(item.value_id),
    sourceName: item.name,
    sourceType: item.field_key,
    sourceTypeLabel: item.field_label,
    targetId: item.target_id ? String(item.target_id) : '',
    targetName: item.target_name ? directionLabel(item.target_name, item.target_parent_name) : null,
    targetArchived: item.target_archived === true,
    targetUses: Number(item.target_uses || 0),
    sourceStale: item.is_stale === true,
    targetStale: item.target_stale === true,
    isNew: item.is_new === true,
    origin: item.origin
  }
}

function directionTargetOptions (items) {
  return items.map(item => ({
    id: String(item.id),
    name: directionLabel(item.name || 'Название отсутствует', item.parent_name),
    archived: item.archived === true,
    unavailable: Number(item.uses || 0) > 0
  }))
}

function plainRows (count) {
  return count === 1 ? 'строке' : 'строках'
}

function targetOptions (items) {
  return items.map(item => ({
    id: String(item.fintablo_partner_id),
    name: item.name || 'Название отсутствует',
    unavailable: item.is_mapped === true
  }))
}

const TARGET_RESULTS_LIMIT = 50

function ReferenceTargetCombobox ({ disabled, label, onSelect, options, value }) {
  return (
    <SearchableSelect
      aria-label={label}
      className='reference-editor__combobox'
      disabled={disabled}
      inputClassName='reference-editor__combobox-input'
      maxResults={TARGET_RESULTS_LIMIT}
      onChange={onSelect}
      optionClassName='reference-editor__option'
      options={options.map(option => ({
        value: option.id,
        label: option.name,
        disabled: option.disabled
      }))}
      placeholder='Выберите или начните вводить'
      value={value}
    />
  )
}

const FunnelIcon = () => (
  <svg aria-hidden='true' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <path d='M2 3h12l-4.5 5.5V13l-3 1.5v-6L2 3z' />
  </svg>
)

const TrashIcon = () => (
  <svg aria-hidden='true' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.4'>
    <path d='M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5' />
  </svg>
)

const PlusIcon = () => (
  <svg aria-hidden='true' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.7'>
    <path d='M8 3v10M3 8h10' />
  </svg>
)

const ReloadIcon = () => (
  <svg aria-hidden='true' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.6'>
    <path d='M13 8a5 5 0 1 1-1.5-3.6M13 2.5V5h-2.5' />
  </svg>
)

// Фильтр колонки — иконкой в заголовке, как в справочниках Финтабло
// (решение владельца 2026-10-01). Закрывается выбором, Escape и кликом мимо.
function ColumnFilter ({ active, children, label, onClose, onToggle, open }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onMouseDown = event => { if (ref.current && !ref.current.contains(event.target)) onClose() }
    const onKeyDown = event => { if (event.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])
  return (
    <span className='reference-editor__column-filter' ref={ref}>
      <button
        aria-expanded={open}
        aria-label={label}
        className='reference-editor__filter-button'
        data-active={active || undefined}
        onClick={onToggle}
        type='button'
      >
        <FunnelIcon />
      </button>
      {open && <div className='reference-editor__popover' role='dialog' aria-label={label}>{children}</div>}
    </span>
  )
}

// Справочник контрагентов большого аккаунта (counterparty-large-accounts):
// страница, вкладка и поиск — на сервере, загрузка компаний — порциями.
function counterpartyAdapter ({ slug, crmName, connectionId, list, partners, sync, save, remove, search, add, review, toRow, toSaved }) {
  return {
    paged: true,
    crmName,
    tabStorageKey: `${TAB_STORAGE_PREFIX}:${slug}:${connectionId}`,
    title: 'Контрагенты',
    sourceLabel: `Контрагент ${crmName}`,
    targetLabel: 'Контрагент Финтабло',
    async loadPage (params) {
      const data = await list(connectionId, params)
      return {
        rows: (data.mappings || []).map(toRow),
        counts: data.counts || { mapped: 0, unmapped: 0, all: 0, new: 0 },
        page: data.page || { number: params.page, size: PAGE_SIZE, has_more: false },
        load: data.load || null,
        generatedAt: data.generated_at || null
      }
    },
    async loadTargets () {
      const data = await partners(connectionId)
      return targetOptions(data.partners || [])
    },
    sync: mode => sync(connectionId, mode),
    async save (changes) {
      await save(connectionId, changes.map(change => toSaved(change.row, change.targetId)))
      return []
    },
    delete: row => remove(connectionId, row.sourceType, row.sourceId),
    search: q => search(connectionId, q),
    add: item => add(connectionId, [{ entity_type: item.entity_type, entity_id: item.entity_id }]),
    markReviewed: seenAt => review(connectionId, seenAt)
  }
}

// Справочник «Направления»: экран один, у коннектора свои адреса API
// (direction-by-pipeline, amocrm-direction-reference).
const DIRECTION_API = {
  bitrix24: {
    crmName: 'Bitrix24',
    list: 'getBitrix24DirectionMappings',
    targets: 'getBitrix24FintabloDirections',
    sync: 'syncBitrix24Directions',
    save: 'saveBitrix24DirectionMappingsBatch',
    remove: 'deleteBitrix24DirectionMapping',
    review: 'markBitrix24DirectionsReviewed',
    settings: 'setBitrix24DirectionSettings'
  },
  amocrm: {
    crmName: 'amoCRM',
    list: 'getAmocrmDirectionMappings',
    targets: 'getAmocrmFintabloDirections',
    sync: 'syncAmocrmDirections',
    save: 'saveAmocrmDirectionMappingsBatch',
    remove: 'deleteAmocrmDirectionMapping',
    review: 'markAmocrmDirectionsReviewed',
    settings: 'setAmocrmDirectionSettings'
  }
}

function directionAdapter (slug, connectionId) {
  const crm = DIRECTION_API[slug]
  return {
    paged: true,
    kind: 'directions',
    crmName: crm.crmName,
    tabStorageKey: `${TAB_STORAGE_PREFIX}:directions:${slug}:${connectionId}`,
    title: 'Направления',
    sourceLabel: `Значение ${crm.crmName}`,
    targetLabel: 'Направление Финтабло',
    syncLabel: 'Синхронизировать направления',
    deleteTitle: `Удалить сопоставление. Значение в ${crm.crmName} и направление в Финтабло не удаляются.`,
    async loadPage (params) {
      const data = await api[crm.list](connectionId, params)
      return {
        rows: (data.mappings || []).map(directionRow),
        counts: data.counts || { mapped: 0, unmapped: 0, all: 0, new: 0 },
        page: data.page || { number: params.page, size: PAGE_SIZE, has_more: false },
        load: null,
        generatedAt: data.generated_at || null,
        fields: data.fields || [],
        multiSelect: data.multi_select !== false,
        syncedAt: data.directions_synced_at || null
      }
    },
    async loadTargets () {
      const data = await api[crm.targets](connectionId)
      return directionTargetOptions(data.directions || [])
    },
    sync: () => api[crm.sync](connectionId),
    async save (changes) {
      await api[crm.save](connectionId, changes.map(change => ({
        field_key: change.row.sourceType,
        value_id: change.row.sourceId,
        fintablo_direction_id: change.targetId
      })))
      return []
    },
    delete: row => api[crm.remove](connectionId, row.sourceType, row.sourceId),
    markReviewed: seenAt => api[crm.review](connectionId, seenAt),
    setMultiSelect: value => api[crm.settings](connectionId, value)
  }
}

// Итог синхронизации направлений: автоподбора нет, поэтому «сопоставлено
// автоматически» не называется. Отказ Финтабло — прежний список.
function directionSyncSummary (result, crmName) {
  const count = result.amocrm_count ?? result.b24_count
  const crm = `Получено из ${crmName}: ${formatCount(count)}, новых — ${formatCount(result.new_count)}.`
  return result.fintablo_stale
    ? `${crm} Финтабло не ответил — список направлений прежний, повторите позже.`
    : `${crm} Направлений Финтабло: ${formatCount(result.fintablo_count)}.`
}

function formatSyncedAt (value) {
  if (!value) return 'Направления Финтабло ещё не загружены'
  const date = new Date(value)
  return `Направления Финтабло: обновлены ${date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')}`
}

function createAdapter ({ slug, type, userId, connectionId }) {
  if (type === 'directions' && DIRECTION_API[slug]) return directionAdapter(slug, connectionId)

  if (slug === 'amocrm' && type === 'employees') {
    return {
      title: 'Сотрудники',
      sourceLabel: 'Сотрудник amoCRM',
      targetLabel: 'Сотрудник Финтабло',
      syncLabel: 'Синхронизировать сотрудников',
      async load () {
        const [mappingData, targetData] = await Promise.all([
          api.getEmployeeMappings(userId),
          api.getFintabloEmployees(userId)
        ])
        return {
          rows: (mappingData.mappings || []).map(item => employeeRow(item, 'amocrm')),
          targets: targetOptions(targetData.employees || [])
        }
      },
      sync: () => api.syncEmployees(userId),
      async save (changes) {
        const results = await Promise.allSettled(changes.map(change =>
          api.saveEmployeeMapping(userId, change.row.sourceId, change.targetId)
        ))
        return results.flatMap((result, index) => result.status === 'rejected'
          ? [{ key: changes[index].row.key, message: result.reason?.message || 'Не удалось сохранить' }]
          : [])
      },
      delete: row => api.deleteEmployeeMapping(userId, row.sourceId)
    }
  }

  if (slug === 'bitrix24' && type === 'employees') {
    return {
      title: 'Сотрудники',
      sourceLabel: 'Сотрудник Bitrix24',
      targetLabel: 'Сотрудник Финтабло',
      syncLabel: 'Синхронизировать сотрудников',
      async load () {
        const [mappingData, targetData] = await Promise.all([
          api.getBitrix24EmployeeMappings(connectionId),
          api.getBitrix24FintabloEmployees(connectionId)
        ])
        return {
          rows: (mappingData.mappings || []).map(item => employeeRow(item, 'bitrix24')),
          targets: targetOptions(targetData.employees || [])
        }
      },
      sync: () => api.syncBitrix24Employees(connectionId),
      async save (changes) {
        await api.saveBitrix24EmployeeMappingsBatch(connectionId, changes.map(change => ({
          b24_user_id: change.row.sourceId,
          fintablo_partner_id: change.targetId
        })))
        return []
      },
      delete: row => api.deleteBitrix24EmployeeMapping(connectionId, row.sourceId)
    }
  }

  if (slug === 'amocrm' && type === 'counterparties') {
    return counterpartyAdapter({
      slug,
      crmName: 'amoCRM',
      connectionId,
      list: api.getAmocrmCounterpartyMappings,
      partners: api.getAmocrmFintabloPartners,
      sync: api.syncAmocrmCounterparties,
      save: api.saveAmocrmCounterpartyMappingsBatch,
      remove: api.deleteAmocrmCounterpartyMapping,
      search: api.searchAmocrmCounterpartiesInCrm,
      add: api.addAmocrmCounterpartiesFromCrm,
      review: api.markAmocrmCounterpartiesReviewed,
      toRow: amocrmCounterpartyRow,
      toSaved: (row, targetId) => ({ entity_type: row.sourceType, entity_id: row.sourceId, fintablo_partner_id: targetId })
    })
  }

  if (slug === 'bitrix24' && type === 'counterparties') {
    return counterpartyAdapter({
      slug,
      crmName: 'Bitrix24',
      connectionId,
      list: api.getBitrix24CounterpartyMappings,
      partners: api.getBitrix24FintabloPartners,
      sync: api.syncBitrix24Counterparties,
      save: api.saveBitrix24CounterpartyMappingsBatch,
      remove: api.deleteBitrix24CounterpartyMapping,
      search: api.searchBitrix24CounterpartiesInCrm,
      add: api.addBitrix24CounterpartiesFromCrm,
      review: api.markBitrix24CounterpartiesReviewed,
      toRow: counterpartyRow,
      toSaved: (row, targetId) => ({ b24_entity_type: row.sourceType, b24_entity_id: row.sourceId, fintablo_partner_id: targetId })
    })
  }

  return null
}

function saveLabel (count) {
  if (count === 1) return 'Сохранить 1 сопоставление'
  if (count > 1 && count < 5) return `Сохранить ${count} сопоставления`
  return `Сохранить ${count} сопоставлений`
}

function syncSummary (result) {
  const sourceCount = result.amocrm_count ?? result.b24_count ?? 0
  return `Получено из CRM: ${sourceCount}. Получено из Финтабло: ${result.fintablo_count ?? 0}. Сопоставлено автоматически: ${result.matched_count ?? 0}.`
}

// Итог порции загрузки компаний: сколько пришло и сколько из них новых,
// сколько всего и есть ли ещё. «Новых» отличает свежую загрузку, которая
// ничего не добавила, от загрузки с новыми компаниями.
function portionSummary (result, mode) {
  const loaded = formatCount(result.companies_count)
  const fresh = formatCount(result.new_count)
  const total = formatCount(result.loaded_total)
  const portion = result.has_more
    ? `${mode === 'more' ? 'Загружено ещё' : 'Загружено'} ${loaded} компаний, новых — ${fresh}, всего ${total}. Есть ещё — можно загрузить.`
    : `Загружены все компании: ${total}, новых — ${fresh}.`
  return `${portion} Сопоставлено автоматически: ${formatCount(result.matched_count)}.`
}

// Номера страниц: первые три, последняя и соседние с текущей; разрывы — «…».
function pageNumbers (current, total) {
  const numbers = []
  for (let number = 1; number <= total; number++) {
    if (number <= 3 || number === total || Math.abs(number - current) <= 1) numbers.push(number)
  }
  return numbers
}

// Одна кнопка загрузки (решение владельца 2026-10-01). Проход живёт в пределах
// захода на страницу: первое нажатие начинает со свежих и приносит новые
// компании, следующие идут вглубь, пока есть что загружать. Новый заход — снова
// со свежих: курсор прежнего захода не продолжается.
function loadStatus (load) {
  const companies = formatCount(load?.companies)
  return `Компании: ${companies}${load?.crm_total ? ` из ≈ ${formatCount(load.crm_total)}` : ''}`
}

export default function ReferenceMappingEditor ({ user }) {
  const { slug, type } = useParams()
  const adapterRef = useRef(null)
  const activeRef = useRef(true)
  const listRequestRef = useRef(0)
  // Строки с несохранённым выбором: на другой странице их уже не видно, а
  // сохранить выбор нужно.
  const pendingRowsRef = useRef(new Map())
  const [hub, setHub] = useState(null)
  const [meta, setMeta] = useState(null)
  const [rows, setRows] = useState([])
  const [targets, setTargets] = useState([])
  const [pending, setPending] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [deleting, setDeleting] = useState({})
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  // Постраничный справочник контрагентов.
  const [tab, setTab] = useState(DEFAULT_TAB)
  const [filterText, setFilterText] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [mappingFilter, setMappingFilter] = useState('')
  const [openFilter, setOpenFilter] = useState(null)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)
  const [counts, setCounts] = useState(null)
  const [pageInfo, setPageInfo] = useState(null)
  const [loadState, setLoadState] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [loadNotice, setLoadNotice] = useState(null)
  const [crmSearch, setCrmSearch] = useState(null)
  // Проход загрузки начат в этом заходе на страницу и не дошёл до конца.
  const [passHasMore, setPassHasMore] = useState(false)
  // Момент, на который получен показанный список, и отметка «просмотрено».
  const [generatedAt, setGeneratedAt] = useState(null)
  const [reviewState, setReviewState] = useState(null)
  // Справочник «Направления»: поля сделки для фильтра по типу, переключатель
  // множественного выбора и время списка направлений Финтабло.
  const [fields, setFields] = useState([])
  const [multiSelect, setMultiSelect] = useState(null)
  const [toggleError, setToggleError] = useState(null)
  const [toggleSaving, setToggleSaving] = useState(false)
  const [syncedAt, setSyncedAt] = useState(null)

  async function refresh (adapter = adapterRef.current) {
    if (adapter.paged) {
      const nextTargets = await adapter.loadTargets()
      if (!activeRef.current) return
      setTargets(nextTargets)
      setReloadKey(key => key + 1)
      return
    }
    const data = await adapter.load()
    if (!activeRef.current) return
    setRows(data.rows)
    setTargets(data.targets)
  }

  useEffect(() => {
    activeRef.current = true
    setLoading(true)
    setError(null)
    setNotice(null)
    setPending({})
    pendingRowsRef.current = new Map()
    setRowErrors({})
    setTab(DEFAULT_TAB)
    setFilterText('')
    setTypeFilter('')
    setMappingFilter('')
    setOpenFilter(null)
    setQuery('')
    setPage(1)
    setCounts(null)
    setPageInfo(null)
    setLoadState(null)
    setLoadError(null)
    setLoadNotice(null)
    setCrmSearch(null)
    setPassHasMore(false)
    setFields([])
    setMultiSelect(null)
    setToggleError(null)

    api.getConnectorHub(slug)
      .then(async hubData => {
        if (!hubData.connection) throw new Error('Сначала подключите источник')
        const adapter = createAdapter({
          slug,
          type,
          userId: user.id,
          connectionId: hubData.connection.id
        })
        if (!adapter || !(hubData.references || []).some(reference => reference.type === type)) {
          throw new Error('Справочник не поддерживается')
        }
        adapterRef.current = adapter
        if (!activeRef.current) return
        if (adapter.paged) setTab(storedTab(adapter.tabStorageKey) || DEFAULT_TAB)
        setHub(hubData)
        setMeta(adapter)
        await refresh(adapter)
      })
      .catch(err => {
        if (activeRef.current) setError(err.message)
      })
      .finally(() => {
        if (activeRef.current) setLoading(false)
      })

    return () => {
      activeRef.current = false
      adapterRef.current = null
    }
  }, [slug, type, user.id])

  // Страница справочника: вкладка, поиск и номер страницы — на сервере.
  // Ответ на устаревший запрос (вкладку уже сменили) не применяется.
  useEffect(() => {
    if (!meta?.paged) return
    const requestId = ++listRequestRef.current
    setReviewState(null)
    meta.loadPage({ tab, q: query, page, ...(typeFilter ? { type: typeFilter } : {}) })
      .then(data => {
        if (!activeRef.current || requestId !== listRequestRef.current) return
        setRows(data.rows)
        setCounts(data.counts)
        setPageInfo(data.page)
        setGeneratedAt(data.generatedAt)
        if (data.load) setLoadState(data.load)
        if (data.fields) setFields(data.fields)
        if (data.multiSelect !== undefined) setMultiSelect(data.multiSelect)
        if (data.syncedAt !== undefined) setSyncedAt(data.syncedAt)
      })
      .catch(err => {
        if (activeRef.current && requestId === listRequestRef.current) setError(err.message)
      })
  }, [meta, tab, query, page, typeFilter, reloadKey])

  // Поле «Найти контрагента в справочнике»: запрос к серверу после паузы ввода.
  useEffect(() => {
    if (!meta?.paged) return
    const timer = setTimeout(() => {
      const next = filterText.trim()
      if (next === query) return
      setQuery(next)
      setPage(1)
    }, FILTER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [filterText, meta])

  const liveRows = rows.filter(row => !row.sourceStale || row.targetId)
  // У сотрудников весь справочник уже загружен (десятки строк): фильтры
  // колонок отбирают в браузере. У контрагентов отбирает сервер.
  const localQuery = filterText.trim().toLowerCase()
  const visibleRows = meta?.paged
    ? liveRows
    : liveRows.filter(row =>
      (!localQuery || [row.sourceName, row.sourceDescription].some(value =>
        String(value || '').toLowerCase().includes(localQuery))) &&
      (!mappingFilter || (mappingFilter === 'mapped') === Boolean(row.targetId)))
  const effectiveValue = row => Object.prototype.hasOwnProperty.call(pending, row.key) ? pending[row.key] : row.targetId
  // Сколько строк выбрало каждую запись Финтабло — один проход на таблицу, а
  // не сравнение каждой строки со всеми (на тысячах строк это задержки).
  // Несохранённый выбор на других страницах тоже занимает запись.
  const usedTargets = new Map()
  // Строки, скрытые фильтром, тоже занимают свою запись Финтабло.
  const visibleKeys = new Set(liveRows.map(row => row.key))
  const countTarget = value => { if (value) usedTargets.set(value, (usedTargets.get(value) || 0) + 1) }
  for (const row of liveRows) countTarget(effectiveValue(row))
  for (const [key, value] of Object.entries(pending)) {
    if (!visibleKeys.has(key)) countTarget(value)
  }
  const changes = Object.entries(pending)
    .filter(([key, targetId]) => targetId && pendingRowsRef.current.has(key))
    .map(([key, targetId]) => ({ row: pendingRowsRef.current.get(key), targetId }))

  function updatePending (row, value) {
    setRowErrors(current => {
      const next = { ...current }
      delete next[row.key]
      return next
    })
    if (value === row.targetId) pendingRowsRef.current.delete(row.key)
    else pendingRowsRef.current.set(row.key, row)
    setPending(current => {
      const next = { ...current }
      if (value === row.targetId) delete next[row.key]
      else next[row.key] = value
      return next
    })
  }

  function forgetPending (keys) {
    for (const key of keys) pendingRowsRef.current.delete(key)
  }

  async function handleSync () {
    const adapter = adapterRef.current
    if (!adapter) return
    setSyncing(true)
    setError(null)
    setNotice(null)
    try {
      const result = await adapter.sync()
      setNotice(adapter.kind === 'directions' ? directionSyncSummary(result, adapter.crmName) : syncSummary(result))
      await refresh(adapter)
    } catch (err) {
      setError(err.message)
    } finally {
      setSyncing(false)
    }
  }

  async function handleLoadPortion (mode) {
    const adapter = adapterRef.current
    if (!adapter) return
    setSyncing(true)
    setLoadError(null)
    setLoadNotice(null)
    try {
      const result = await adapter.sync(mode)
      if (!activeRef.current) return
      setLoadNotice(portionSummary(result, mode))
      setPassHasMore(result.has_more === true)
      await refresh(adapter)
    } catch (err) {
      if (activeRef.current) setLoadError(err.message)
    } finally {
      if (activeRef.current) setSyncing(false)
    }
  }

  async function handleSave () {
    const adapter = adapterRef.current
    if (!adapter || changes.length === 0) return
    setSaving(true)
    setError(null)
    setNotice(null)
    setRowErrors({})
    try {
      const failures = await adapter.save(changes)
      const failedKeys = new Set(failures.map(failure => failure.key))
      setRowErrors(Object.fromEntries(failures.map(failure => [failure.key, failure.message])))
      forgetPending(changes.map(change => change.row.key).filter(key => !failedKeys.has(key)))
      setPending(current => Object.fromEntries(
        Object.entries(current).filter(([key]) => failedKeys.has(key))
      ))
      if (failures.length === 0) setNotice(`Сохранено сопоставлений: ${changes.length}.`)
      await refresh(adapter)
    } catch (err) {
      setRowErrors(Object.fromEntries(changes.map(change => [change.row.key, err.message])))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete (row) {
    const adapter = adapterRef.current
    if (!adapter || !row.targetId) return
    setDeleting(current => ({ ...current, [row.key]: true }))
    setRowErrors(current => {
      const next = { ...current }
      delete next[row.key]
      return next
    })
    try {
      await adapter.delete(row)
      forgetPending([row.key])
      setPending(current => {
        const next = { ...current }
        delete next[row.key]
        return next
      })
      setNotice('Сопоставление удалено. Сущности CRM и Финтабло не изменены.')
      await refresh(adapter)
    } catch (err) {
      setRowErrors(current => ({ ...current, [row.key]: err.message }))
    } finally {
      setDeleting(current => ({ ...current, [row.key]: false }))
    }
  }

  function handleCrmSearch (text) {
    return adapterRef.current.search(text)
  }

  async function handleCrmAdd (item) {
    const adapter = adapterRef.current
    const result = await adapter.add(item)
    if (!activeRef.current) return
    const name = item.name || 'Запись без названия'
    // Добавленная запись — первой строкой своей вкладки.
    setNotice(result.matched > 0
      ? `«${name}» добавлена в справочник и сопоставлена по названию — она на вкладке «Сопоставленные».`
      : `«${name}» добавлена в справочник — она на вкладке «Несопоставленные», первой строкой.`)
    setTab(result.matched > 0 ? 'mapped' : 'unmapped')
    setFilterText('')
    setQuery('')
    setPage(1)
    await refresh(adapter)
  }

  function showInReference (text) {
    setCrmSearch(null)
    setFilterText(text)
    setQuery(text)
    setTab('all')
    setPage(1)
  }

  function selectMapping (key) {
    setMappingFilter(key)
    setOpenFilter(null)
  }

  function resetNameFilter () {
    setFilterText('')
    setQuery('')
    setPage(1)
  }

  function selectType (key) {
    setTypeFilter(key)
    setPage(1)
    setOpenFilter(null)
  }

  function closeFilter () {
    setOpenFilter(null)
  }

  function selectTab (key) {
    setTab(key)
    setPage(1)
    if (meta?.tabStorageKey) rememberTab(meta.tabStorageKey, key)
  }

  // «Отметить просмотренными» (решение владельца 2026-10-01): новые на момент
  // показанного списка перестают быть новыми; список на экране остаётся до
  // смены вкладки. Сохранение сопоставлений «Новые» не сбрасывает.
  async function handleMarkReviewed () {
    const adapter = adapterRef.current
    if (!adapter || !generatedAt) return
    setReviewState('saving')
    try {
      await adapter.markReviewed(generatedAt)
      if (activeRef.current) setReviewState('done')
    } catch (err) {
      if (activeRef.current) {
        setReviewState(null)
        setError(err.message)
      }
    }
  }

  // Переключатель множественного выбора. Выключить с повтором сервер не даёт
  // и называет направления — текст показывается под переключателем.
  async function handleToggleMultiSelect () {
    const adapter = adapterRef.current
    if (!adapter?.setMultiSelect || multiSelect === null) return
    const next = !multiSelect
    setToggleSaving(true)
    setToggleError(null)
    try {
      await adapter.setMultiSelect(next)
      if (!activeRef.current) return
      setMultiSelect(next)
      setReloadKey(key => key + 1)
    } catch (err) {
      if (activeRef.current) setToggleError(err.message)
    } finally {
      if (activeRef.current) setToggleSaving(false)
    }
  }

  if (loading) return <Skeleton className='reference-editor__skeleton' aria-label='Загрузка справочника' />

  // Ссылка в хаб остаётся и на экране ошибки, который заменяет страницу.
  if (error && !meta) {
    return (
      <div className='reference-editor'>
        <Link className='reference-editor__back' to={ROUTES.INTEGRATION(slug)}>Хаб коннектора</Link>
        <InlineAlert title='Не удалось открыть справочник' tone='error'>{error}</InlineAlert>
      </div>
    )
  }

  const paged = meta.paged === true
  const total = paged && counts ? counts[tab] || 0 : 0
  const totalPages = paged ? Math.max(Math.ceil(total / PAGE_SIZE), page + (pageInfo?.has_more ? 1 : 0), 1) : 1
  const firstOnPage = (page - 1) * PAGE_SIZE + 1
  const referenceEmpty = paged && counts && counts.all === 0 && !query && !typeFilter
  const domain = hub.connection.display_name || hub.connector.name
  const nameFilter = paged ? query : filterText.trim()
  const directions = meta.kind === 'directions'
  // У направлений повтор разрешён, пока переключатель включён.
  const repeatsAllowed = directions && multiSelect !== false
  const typeFilters = directions
    ? [{ key: '', label: 'Все типы' }, ...fields.map(field => ({ key: field.key, label: field.label }))]
    : TYPE_FILTERS
  const typeFilterLabel = directions
    ? fields.find(field => field.key === typeFilter)?.label || typeFilter
    : TYPE_FILTER_LABELS[typeFilter]

  function renderRows () {
    return visibleRows.map(row => {
      const value = effectiveValue(row)
      const baseOptions = row.targetId && !targets.some(target => target.id === row.targetId)
        ? [{ id: row.targetId, name: row.targetName || 'Недоступная запись Финтабло' }, ...targets]
        : targets
      const options = baseOptions
        // Архивное направление в новом выборе не предлагается; уже выбранное
        // остаётся в своей строке.
        .filter(option => !option.archived || option.id === row.targetId || option.id === value)
        .map(option => ({
          ...option,
          // Занято другой строкой: выбрано где-то ещё, кроме этой строки.
          disabled: !repeatsAllowed && option.id !== value && (option.unavailable || (usedTargets.get(option.id) || 0) > 0)
        }))
      const changed = Object.prototype.hasOwnProperty.call(pending, row.key)
      return (
        <div
          className='reference-editor__row-group'
          data-reference-row
          data-stale={row.sourceStale || row.targetStale || row.targetConflict || undefined}
          key={row.key}
        >
          <div className='reference-editor__row' role='row'>
            <div className='reference-editor__source' role='cell' data-label={meta.sourceLabel}>
              <span className='reference-editor__name'>{row.sourceName || 'Название отсутствует'}</span>
              {row.sourceDescription && <span>{row.sourceDescription}</span>}
              {row.isNew && !row.targetId && (
                <Status tone='warning'>{['новая', ORIGIN_LABELS[row.origin]].filter(Boolean).join(' · ')}</Status>
              )}
            </div>
            {paged && (
              <div className='reference-editor__type' role='cell' data-label='Тип'>
                {row.sourceTypeLabel || TYPE_LABELS[row.sourceType] || row.sourceType || ''}
              </div>
            )}
            <div className='reference-editor__target' role='cell' data-label={meta.targetLabel} data-changed={changed || undefined}>
              <ReferenceTargetCombobox
                disabled={row.sourceStale || saving}
                label={`Сопоставление для ${row.sourceName || 'записи без названия'}`}
                onSelect={targetId => updatePending(row, targetId)}
                options={options}
                value={value}
              />
              {directions && !changed && row.targetArchived && <Status>в архиве</Status>}
              {repeatsAllowed && !changed && row.targetUses > 0 && (
                <Status tone='info'>ещё в {row.targetUses} {plainRows(row.targetUses)}</Status>
              )}
            </div>
            <div className='reference-editor__actions' role='cell'>
              {row.targetId && (
                <Button
                  variant='ghost'
                  className='reference-editor__delete'
                  loading={Boolean(deleting[row.key])}
                  aria-label={`Удалить сопоставление: ${row.sourceName || 'запись без названия'}`}
                  title={meta.deleteTitle || 'Удалить сопоставление. Запись в CRM и контрагент в Финтабло не удаляются.'}
                  onClick={() => handleDelete(row)}
                >
                  <TrashIcon />
                </Button>
              )}
            </div>
          </div>
          {row.sourceStale && <div className='reference-editor__stale' role='status'>Источник удалён</div>}
          {row.targetStale && <div className='reference-editor__stale' role='status'>Запись Финтабло удалена</div>}
          {row.targetConflict && <div className='reference-editor__stale' role='status'>Запись Финтабло используется в другом сопоставлении</div>}
          {rowErrors[row.key] && <div className='reference-editor__row-error' role='alert'>{rowErrors[row.key]}</div>}
        </div>
      )
    })
  }

  function renderHeader () {
    return (
      <div className='reference-editor__header' role='row'>
        <div role='columnheader'>
          {meta.sourceLabel}
          <ColumnFilter
            active={Boolean(nameFilter)}
            label='Фильтр по названию'
            onClose={closeFilter}
            onToggle={() => setOpenFilter(current => current === 'name' ? null : 'name')}
            open={openFilter === 'name'}
          >
            <input
              aria-label={paged && !directions ? 'Найти контрагента в справочнике' : 'Найти в справочнике'}
              autoComplete='off'
              autoFocus
              className='ui-input reference-editor__popover-input'
              id='reference-editor-filter'
              onChange={event => setFilterText(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') closeFilter() }}
              placeholder='Начните вводить название'
              type='search'
              value={filterText}
            />
          </ColumnFilter>
        </div>
        {paged && (
          <div role='columnheader'>
            Тип
            <ColumnFilter
              active={Boolean(typeFilter)}
              label='Фильтр по типу'
              onClose={closeFilter}
              onToggle={() => setOpenFilter(current => current === 'type' ? null : 'type')}
              open={openFilter === 'type'}
            >
              {typeFilters.map(item => (
                <button
                  aria-pressed={typeFilter === item.key}
                  className='reference-editor__popover-option'
                  key={item.key || 'all'}
                  onClick={() => selectType(item.key)}
                  type='button'
                >
                  {item.label}
                </button>
              ))}
            </ColumnFilter>
          </div>
        )}
        <div role='columnheader'>
          {meta.targetLabel}
          {!paged && (
            <ColumnFilter
              active={Boolean(mappingFilter)}
              label='Фильтр по сопоставлению'
              onClose={closeFilter}
              onToggle={() => setOpenFilter(current => current === 'mapping' ? null : 'mapping')}
              open={openFilter === 'mapping'}
            >
              {MAPPING_FILTERS.map(item => (
                <button
                  aria-pressed={mappingFilter === item.key}
                  className='reference-editor__popover-option'
                  key={item.key || 'all'}
                  onClick={() => selectMapping(item.key)}
                  type='button'
                >
                  {item.label}
                </button>
              ))}
            </ColumnFilter>
          )}
        </div>
        <div role='columnheader'><span className='ui-sr-only'>Действия</span></div>
      </div>
    )
  }

  function renderTable () {
    return (
      <div className={`reference-editor__table${paged ? ' reference-editor__table--typed' : ''}`} role='table' aria-label={meta.title}>
        {renderHeader()}
        {visibleRows.length === 0 ? renderPagedEmpty() : renderRows()}
      </div>
    )
  }

  function renderPagedEmpty () {
    if (!paged) return <div className='reference-editor__empty'><p>По фильтру никого нет.</p></div>
    if (directions && (query || typeFilter)) {
      return <div className='reference-editor__empty'><p>По фильтру ничего нет.</p></div>
    }
    if (query) {
      return (
        <div className='reference-editor__empty'>
          <p>В справочнике нет «{query}».</p>
          <p className='reference-editor__muted'>Возможно, запись ещё не загружена.</p>
          <Button onClick={() => setCrmSearch({ query })}>Добавить «{query}» через поиск в {meta.crmName}</Button>
        </div>
      )
    }
    const text = tab === 'mapped'
      ? 'Сопоставленных пока нет.'
      : tab === 'new'
        ? 'Новых несопоставленных нет: после последней отметки «просмотрено» никто не появился.'
        : 'Здесь пусто.'
    return <div className='reference-editor__empty'><p>{text}</p></div>
  }

  function renderPanel () {
    const loadMode = passHasMore ? 'more' : null
    return (
      <section className='reference-editor__panel' aria-label='Управление справочником'>
        <div className='reference-editor__panel-row'>
          <Button disabled={changes.length === 0} loading={saving} onClick={handleSave}>
            {saveLabel(changes.length)}
          </Button>
          {directions && (
            <>
              <Button variant='secondary' iconLeft={<ReloadIcon />} loading={syncing} onClick={handleSync}>
                {meta.syncLabel}
              </Button>
              <div className='reference-editor__status' role='status'>{formatSyncedAt(syncedAt)}</div>
              {multiSelect !== null && (
                <div className='reference-editor__multi'>
                  <button
                    aria-checked={multiSelect}
                    aria-labelledby='reference-editor-multi-hint'
                    className='reference-editor__switch'
                    disabled={toggleSaving}
                    onClick={handleToggleMultiSelect}
                    role='switch'
                    type='button'
                  />
                  <span id='reference-editor-multi-hint'>{multiSelect ? MULTI_SELECT_HINTS.on : MULTI_SELECT_HINTS.off}</span>
                </div>
              )}
            </>
          )}
          {!directions && (paged
            ? (
              <>
                <Button variant='stroke' iconLeft={<PlusIcon />} onClick={() => setCrmSearch({ query: '' })}>
                  Добавить через поиск в {meta.crmName}
                </Button>
                <Button variant='secondary' iconLeft={<ReloadIcon />} loading={syncing} onClick={() => handleLoadPortion(loadMode)}>
                  {passHasMore ? 'Загрузить ещё компании' : 'Загрузить компании'}
                </Button>
                <div className='reference-editor__status' role='status'>
                  {syncing ? 'Загрузка идёт около 8 секунд.' : loadNotice || loadStatus(loadState)}
                </div>
              </>
              )
            : (
              <Button variant='secondary' iconLeft={<ReloadIcon />} loading={syncing} onClick={handleSync}>
                {meta.syncLabel}
              </Button>
              ))}
        </div>
        {toggleError && <div className='reference-editor__row-error' role='alert'>{toggleError}</div>}
        <div className='reference-editor__panel-row reference-editor__panel-row--filters'>
          {nameFilter && (
            <span className='reference-editor__chip'>
              <span>Название: {nameFilter}</span>
              <button aria-label='Сбросить фильтр по названию' onClick={resetNameFilter} type='button'>×</button>
            </span>
          )}
          {typeFilter && (
            <span className='reference-editor__chip'>
              <span>Тип: {typeFilterLabel}</span>
              <button aria-label='Сбросить фильтр по типу' onClick={() => selectType('')} type='button'>×</button>
            </span>
          )}
          {mappingFilter && (
            <span className='reference-editor__chip'>
              <span>Сопоставление: {MAPPING_FILTER_LABELS[mappingFilter]}</span>
              <button aria-label='Сбросить фильтр по сопоставлению' onClick={() => selectMapping('')} type='button'>×</button>
            </span>
          )}
          {/* Место под отметку занято всегда: появление ссылки ничего не сдвигает. */}
          <div className='reference-editor__review-slot'>
            {tab === 'new' && counts?.new > 0 && reviewState !== 'done' && (
              <Button
                className='reference-editor__review'
                loading={reviewState === 'saving'}
                onClick={handleMarkReviewed}
                variant='ghost'
              >
                Отметить просмотренными
              </Button>
            )}
            {tab === 'new' && reviewState === 'done' && (
              <span className='reference-editor__muted' role='status'>
                Отмечено: при следующем открытии этих записей во вкладке не будет.
              </span>
            )}
          </div>
        </div>
        {loadError && <InlineAlert title='Не удалось загрузить компании' tone='error'>{loadError}</InlineAlert>}
      </section>
    )
  }

  // Справочник сотрудников: та же шапка (без вкладок), панель и таблица, что
  // у контрагентов (решение владельца 2026-10-01).
  if (!paged) {
    return (
      <div className='reference-editor'>
        <header className='reference-editor__head reference-editor__head--plain'>
          <Link className='reference-editor__back' to={ROUTES.INTEGRATION(slug)}>Хаб коннектора</Link>
          <div className='reference-editor__title-row'>
            <h1>{meta.title}</h1>
            <span className='reference-editor__domain'>{domain}</span>
          </div>
        </header>
        {renderPanel()}
        {error && <InlineAlert title='Не удалось синхронизировать справочник' tone='error'>{error}</InlineAlert>}
        {notice && <InlineAlert title='Готово' tone='success'>{notice}</InlineAlert>}
        {liveRows.length === 0
          ? <EmptyState title='Справочник пока пуст' description={`Нажмите «${meta.syncLabel}», чтобы загрузить данные.`} />
          : <div className='reference-editor__sheet'>{renderTable()}</div>}
      </div>
    )
  }

  // Справочник контрагентов: шапка с вкладками, закреплённая панель
  // управления и таблица — по образцу экранов Финтабло (прототип 2026-10-01).
  return (
    <div className='reference-editor'>
      <header className='reference-editor__head'>
        <Link className='reference-editor__back' to={ROUTES.INTEGRATION(slug)}>Хаб коннектора</Link>
        <div className='reference-editor__title-row'>
          <h1>{meta.title}</h1>
          <span className='reference-editor__domain'>{domain}</span>
        </div>
        <div className='reference-editor__tabs' role='tablist' aria-label='Отбор справочника'>
          {COUNTERPARTY_TABS.map(item => (
            <button
              aria-selected={tab === item.key}
              className='reference-editor__tab'
              data-attention={item.key === 'new' && counts?.new > 0 ? 'true' : undefined}
              key={item.key}
              onClick={() => selectTab(item.key)}
              role='tab'
              type='button'
            >
              <span>{item.label}</span>
              <span className='reference-editor__tab-count'>{formatCount(counts?.[item.key])}</span>
            </button>
          ))}
        </div>
      </header>

      {renderPanel()}

      {error && <InlineAlert title='Не удалось загрузить справочник' tone='error'>{error}</InlineAlert>}
      {notice && <InlineAlert title='Готово' tone='success'>{notice}</InlineAlert>}

      {!counts && <Skeleton className='reference-editor__skeleton' aria-label='Загрузка справочника' />}
      {referenceEmpty && (
        <EmptyState
          title='Справочник пока пуст'
          description={directions
            ? `Нажмите «${meta.syncLabel}», чтобы загрузить данные.`
            : `Нажмите «Загрузить компании» или добавьте контрагента через поиск в ${meta.crmName}.`}
        />
      )}
      {counts && !referenceEmpty && (
        <div className='reference-editor__sheet'>
          {renderTable()}
          {(total > 0 || totalPages > 1) && (
            <nav className='reference-editor__pager' aria-label='Страницы справочника'>
              <span>{formatCount(firstOnPage)}–{formatCount(firstOnPage + rows.length - 1)} из {formatCount(total)}</span>
              <div className='reference-editor__pages'>
                {pageNumbers(page, totalPages).map((number, index, numbers) => (
                  <React.Fragment key={number}>
                    {index > 0 && number - numbers[index - 1] > 1 && <span aria-hidden='true'>…</span>}
                    <button
                      aria-current={number === page ? 'page' : undefined}
                      aria-label={`Страница ${number}`}
                      className='reference-editor__page'
                      onClick={() => setPage(number)}
                      type='button'
                    >
                      {number}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </nav>
          )}
        </div>
      )}

      {crmSearch && (
        <CounterpartyCrmSearch
          crmName={meta.crmName}
          initialQuery={crmSearch.query}
          onAdd={handleCrmAdd}
          onClose={() => setCrmSearch(null)}
          onSearch={handleCrmSearch}
          onShowInReference={showInReference}
        />
      )}
    </div>
  )
}
